/**
 * Filtering, counting and sorting for a register whose rows all arrive at
 * once (service charges, orders). Registers that page on the server keep
 * their own params helpers instead.
 */

export interface RegisterView<T> {
  key: string;
  label: string;
  /** Omitted for the "All" view. */
  match?: (row: T) => boolean;
}

export interface RegisterSort<T> {
  key: string;
  label: string;
  compare: (a: T, b: T) => number;
}

export interface RegisterConfig<T> {
  /** Text the search box looks through. */
  search: (row: T) => string;
  /** The switch above the filters; the first is the default. */
  views: RegisterView<T>[];
  /** A day on each row (YYYY-MM-DD…) for the From/To filter. */
  date?: { label: string; value: (row: T) => string | null | undefined };
  /** A pick-list of the values found on the rows, e.g. each learner's class. */
  /** `inRowOrder` keeps the values in the order the rows came, e.g. levels in school order. */
  category?: { label: string; value: (row: T) => string | null | undefined; inRowOrder?: boolean };
  /** The first is the default. */
  sorts: RegisterSort<T>[];
}

export interface RegisterState {
  search: string;
  view: string;
  dateFrom: string;
  dateTo: string;
  /** "" for every value of the config's category. */
  category: string;
  sort: string;
}

export const initialRegisterState = <T,>(config: RegisterConfig<T>): RegisterState => ({
  search: "",
  view: config.views[0]?.key ?? "",
  dateFrom: "",
  dateTo: "",
  category: "",
  sort: config.sorts[0]?.key ?? "",
});

/** Narrowing filters in use; the view switch and the sort don't count. */
export const countRegisterFilters = (state: RegisterState) =>
  [state.search.trim(), state.dateFrom || state.dateTo, state.category].filter(Boolean).length;

/** The category values present on the rows, in order, for the pick-list. */
export function categoryOptions<T>(rows: T[], config: RegisterConfig<T>) {
  if (!config.category) return [];
  const values = new Set(rows.map((row) => config.category!.value(row) || "").filter(Boolean));
  if (config.category.inRowOrder) return [...values];
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

export function applyRegister<T>(rows: T[], state: RegisterState, config: RegisterConfig<T>) {
  const search = state.search.trim().toLowerCase();
  const narrowed = rows.filter((row) => {
    if (search && !config.search(row).toLowerCase().includes(search)) return false;
    if (config.category && state.category && config.category.value(row) !== state.category) return false;
    if (config.date && (state.dateFrom || state.dateTo)) {
      const day = config.date.value(row)?.slice(0, 10) ?? "";
      if (!day) return false;
      if (state.dateFrom && day < state.dateFrom) return false;
      if (state.dateTo && day > state.dateTo) return false;
    }
    return true;
  });
  const counts = Object.fromEntries(
    config.views.map((view) => [view.key, view.match ? narrowed.filter(view.match).length : narrowed.length]),
  ) as Record<string, number>;
  const view = config.views.find((entry) => entry.key === state.view);
  const sort = config.sorts.find((entry) => entry.key === state.sort) ?? config.sorts[0];
  const shown = view?.match ? narrowed.filter(view.match) : [...narrowed];
  if (sort) shown.sort(sort.compare);
  return { rows: shown, counts };
}

/** Comparators for the usual register columns. */
export const byText = <T,>(value: (row: T) => string | null | undefined) => (a: T, b: T) =>
  (value(a) ?? "").localeCompare(value(b) ?? "");
export const byNumber = <T,>(value: (row: T) => string | number | null | undefined) => (a: T, b: T) =>
  Number(value(a) ?? 0) - Number(value(b) ?? 0);
export const reversed = <T,>(compare: (a: T, b: T) => number) => (a: T, b: T) => compare(b, a);

/** "part_refunded" → "Part refunded". */
export const humanise = (value: string | number | null | undefined) => {
  // String() because a field typed as text sometimes arrives as an id.
  const text = String(value ?? "").replace(/_/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
};

/** A dot colour for the status words the school's registers use. */
export function statusDot(status: string | null | undefined) {
  switch (status) {
    case "paid":
    case "completed":
    case "active":
    case "served":
    case "delivered":
    case "approved":
      return "bg-emerald-500";
    case "pending":
    case "processing":
    case "confirmed":
    case "reserved":
    case "ready":
      return "bg-amber-500";
    case "overdue":
    case "failed":
    case "rejected":
      return "bg-red-500";
    case "refunded":
    case "part_refunded":
      return "bg-sky-500";
    default:
      return "bg-gray-400";
  }
}

/** "syllabi" → "syllabus", "timetable entries" → "timetable entry", "classes" → "class". */
function singular(name: string) {
  if (name.endsWith("syllabi")) return `${name.slice(0, -1)}us`;
  if (name.endsWith("ies")) return `${name.slice(0, -3)}y`;
  if (/(ss|sh|ch|x)es$/.test(name)) return name.slice(0, -2);
  return name.replace(/s$/, "");
}

/** What a record is still in use by, as a sentence: "3 staff, 1 position". */
export function describeUsage(inUseBy?: Record<string, number>): string {
  return Object.entries(inUseBy ?? {})
    .filter(([, count]) => count > 0)
    // The server names kinds by Django's plural label, sometimes capitalised ("Syllabi").
    .map(([name, count]) => `${count} ${count === 1 ? singular(name.toLowerCase()) : name.toLowerCase()}`)
    .join(", ");
}
